import React, { useContext } from 'react'
import { ResponsiveLine } from '@nivo/line'
import { ThemeContext } from 'styled-components'
import styled from 'styled-components/macro'

const formatCurrency = value => {
  const parsed = Number.parseFloat(value)
  const amount = Number.isNaN(parsed) ? 0 : parsed
  return `£${amount.toFixed(2)}`
}

const formatAxisCurrency = value => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return '£0'
  if (Math.abs(parsed) >= 1000000) {
    return `£${(parsed / 1000000).toFixed(1)}m`
  }
  if (Math.abs(parsed) >= 1000) {
    return `£${(parsed / 1000).toFixed(1)}k`
  }
  return `£${parsed.toFixed(0)}`
}

const LINE_LAYERS = [
  'grid',
  'markers',
  'axes',
  'areas',
  'crosshair',
  'lines',
  'points',
  'slices',
  'mesh',
  'legends',
]

const LineChart = ({ data, venue }) => {
  const theme = useContext(ThemeContext) || {}
  const spendColor = theme?.colours?.omsRed ?? '#EB5757'

  const chartData = [
    {
      id: venue,
      data:
        data && data.length > 0
          ? data.map(item => ({
              x: item.monthName,
              y: parseFloat(item.totalAmountNoVAT),
            }))
          : [],
    },
  ]
  // Under two points nivo has no segment to draw: the line and area paths
  // come out as `d="null"`, so a single month is shown as its point only.
  const canDrawLine = chartData[0].data.length >= 2
  const layers = canDrawLine
    ? LINE_LAYERS
    : LINE_LAYERS.filter(layer => layer !== 'lines' && layer !== 'areas')

  return (
    <ChartContainer>
      <ResponsiveLine
        data={chartData}
        layers={layers}
        margin={{ top: 20, right: 50, bottom: 60, left: 60 }}
        xScale={{ type: 'point' }}
        yScale={{
          type: 'linear',
          min: 0,
          max: 'auto',
          stacked: false,
          reverse: false,
        }}
        yFormat={value => formatCurrency(value)}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          orient: 'bottom',
          tickSize: 0,
          tickPadding: 8,
          tickRotation: data && data.length > 6 ? -25 : 0,
          format: value => value,
        }}
        axisLeft={{
          orient: 'left',
          tickSize: 0,
          tickPadding: 8,
          format: value => formatAxisCurrency(value),
        }}
        enableGridX={false}
        enableGridY={true}
        gridYValues={6}
        colors={[spendColor]}
        lineWidth={3}
        curve={canDrawLine ? 'monotoneX' : 'linear'}
        enablePoints={true}
        pointSize={8}
        pointColor='#FFFFFF'
        pointBorderWidth={2}
        pointBorderColor={spendColor}
        enableArea={canDrawLine}
        areaOpacity={0.08}
        areaBaselineValue={0}
        useMesh={true}
        crosshairType='x'
        enableSlices='x'
        legends={[]}
        minValue={0}
        motionConfig='gentle'
        tooltip={({ point }) => {
          return (
            <ChartTooltip>
              <TooltipDate>{point.data.x}</TooltipDate>
              <TooltipValue>
                <span style={{ color: spendColor }}>Spent: </span>
                <strong>{formatCurrency(point.data.y)}</strong>
              </TooltipValue>
            </ChartTooltip>
          )
        }}
      />
    </ChartContainer>
  )
}

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ChartTooltip = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`

const TooltipDate = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333333;
`

const TooltipValue = styled.div`
  display: flex;
  gap: 4px;
  font-size: 13px;
  align-items: center;
  strong {
    font-weight: 600;
    color: #1a1a1a;
  }
`

export default LineChart
